import { App } from 'antd';
import { useCallback, useRef } from 'react';

interface UseWebSpeechRecognitionOptions {
  onTranscript?: (text: string, isFinal: boolean) => void;
  onError?: (error: string) => void;
  onEnd?: () => void;
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
}

/**
 * Web Speech API 语音识别 Hook
 */
export const useWebSpeechRecognition = (
  options: UseWebSpeechRecognitionOptions = {},
) => {
  const { message: appMessage } = App.useApp();
  const recognition = useRef<SpeechRecognition | null>(null);
  const isInitialized = useRef(false);

  const {
    onTranscript,
    onError,
    onEnd,
    lang = 'zh-CN',
    continuous = true,
    interimResults = true,
  } = options;

  /**
   * 初始化语音识别
   */
  const initialize = useCallback(() => {
    if (isInitialized.current) return true;

    const SpeechRecognition =
      window.webkitSpeechRecognition || window.SpeechRecognition;

    if (!SpeechRecognition) {
      appMessage.error('您的浏览器不支持 Web Speech API');
      return false;
    }

    try {
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = continuous;
      recognition.current.interimResults = interimResults;
      recognition.current.lang = lang;

      // 识别结束回调
      recognition.current.onend = () => {
        console.log('[Web Speech API] 识别结束');
        onEnd?.();
      };

      // 错误回调
      recognition.current.onerror = (event) => {
        console.error('[Web Speech API] 错误:', event.error);
        onError?.(event.error);
      };

      // 识别结果回调
      recognition.current.onresult = (event) => {
        console.log('[Web Speech API] 识别结果事件:', event);
        let interim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          console.log(
            `[Web Speech API] 结果 ${i}:`,
            transcript,
            '是否最终:',
            event.results[i].isFinal,
          );

          if (event.results[i].isFinal) {
            final += transcript;
          } else {
            interim += transcript;
          }
        }

        // 优先返回最终结果
        if (final) {
          console.log('[Web Speech API] 最终结果:', final);
          onTranscript?.(final, true);
        } else if (interim) {
          console.log('[Web Speech API] 临时结果:', interim);
          onTranscript?.(interim, false);
        }
      };

      isInitialized.current = true;
      console.log('[Web Speech API] 初始化成功');
      return true;
    } catch (error) {
      console.error('[Web Speech API] 初始化失败:', error);
      appMessage.error('语音识别初始化失败');
      return false;
    }
  }, [
    lang,
    continuous,
    interimResults,
    onTranscript,
    onError,
    onEnd,
    appMessage,
  ]);

  /**
   * 开始识别
   */
  const start = useCallback(() => {
    if (!initialize()) {
      return false;
    }

    try {
      recognition.current?.start();
      console.log('[Web Speech API] 开始识别');
      return true;
    } catch (error) {
      console.error('[Web Speech API] 启动失败:', error);
      appMessage.error('启动语音识别失败');
      return false;
    }
  }, [initialize, appMessage]);

  /**
   * 停止识别
   */
  const stop = useCallback(() => {
    if (recognition.current) {
      recognition.current.stop();
      console.log('[Web Speech API] 停止识别');
    }
  }, []);

  /**
   * 清理资源
   */
  const cleanup = useCallback(() => {
    if (recognition.current) {
      recognition.current.onend = null;
      recognition.current.onerror = null;
      recognition.current.onresult = null;
      recognition.current = null;
      isInitialized.current = false;
      console.log('[Web Speech API] 清理完成');
    }
  }, []);

  /**
   * 检查浏览器支持
   */
  const isSupported = useCallback(() => {
    return !!(window.webkitSpeechRecognition || window.SpeechRecognition);
  }, []);

  return {
    start,
    stop,
    cleanup,
    isSupported,
  };
};
